import type {
  AdUnitTypeResponse,
  BlacklistBanTypesResponse,
  BlacklistConditionParamsResponse,
  BlacklistStatusesResponse,
} from '@hprofits/api';
import {
  useAdUnitTypes,
  useBlacklistBanTypes,
  useBlacklistConditionParams,
  useBlacklistStatuses,
} from '@hprofits/api-client';
import { compareAdTypeDisplayOrder } from '@hprofits/shared';
import type { ComputedRef } from 'vue';
import { computed } from 'vue';

import { apiClient } from '@/api-client';

export type BlacklistDictionaries = Readonly<{
  types: BlacklistBanTypesResponse;
  states: BlacklistStatusesResponse;
  conditions: BlacklistConditionParamsResponse;
  adUnitTypes: AdUnitTypeResponse;
}>;

export type BlacklistDictionariesQuery = Readonly<{
  data: ComputedRef<BlacklistDictionaries | undefined>;
  isPending: ComputedRef<boolean>;
  isError: ComputedRef<boolean>;
  error: ComputedRef<Error | null>;
  refetch: () => Promise<unknown>;
}>;

// Four independent queries rather than one `Promise.all`: a single hung dictionary used to hold the
// others back with no path to an error, which left the editor on its skeleton for good.
export const useBlacklistDictionaries = (): BlacklistDictionariesQuery => {
  const types = useBlacklistBanTypes(apiClient);
  const states = useBlacklistStatuses(apiClient);
  const conditions = useBlacklistConditionParams(apiClient);
  // `flat`: the editor only lists type names, the formats `full` adds are never read here.
  const adUnitTypes = useAdUnitTypes(apiClient, 'flat');
  const queries = [types, states, conditions, adUnitTypes];

  const data = computed(() => {
    if (
      types.data.value === undefined ||
      states.data.value === undefined ||
      conditions.data.value === undefined ||
      adUnitTypes.data.value === undefined
    ) {
      return undefined;
    }
    return {
      types: types.data.value,
      states: states.data.value,
      conditions: conditions.data.value,
      adUnitTypes: [...adUnitTypes.data.value].sort((a, b) => compareAdTypeDisplayOrder(a.name, b.name)),
    };
  });

  return {
    data,
    isPending: computed(() => queries.some((query) => query.isPending.value)),
    isError: computed(() => queries.some((query) => query.isError.value)),
    error: computed(() => queries.find((query) => query.error.value !== null)?.error.value ?? null),
    refetch: () => Promise.all(queries.map((query) => query.refetch())),
  };
};
